// The deepest gear category the app can navigate to (#1420).
//
// This is NOT a product limit on nesting — Firestore, firestore.rules, the api
// and the Firestore triggers are all depth-agnostic, deliberately. It's the bound
// on the *generated route table*: React Router matches concrete path patterns, so
// `gearRouteDescriptors` / `gearDrawerRoutePaths` (below) and
// `generateGearBreadcrumbPaths` (HeaderBreadcrumbs.js) have to emit a finite
// number of them.
//
// Because navigation is the only thing that breaks past it, it's also the point
// where the UI stops offering "Add subcategory" — a visible, explained cap beats
// a category you can create but never open.
//
// Live max across all tenants is 4 (jrfd-log, sicamousfd) as of Aug 2026.
export const MAX_GEAR_DEPTH = 12;

// The route/breadcrumb generators alternate as they walk down the tree: an even
// iteration i emits CATEGORY routes for depth i/2 + 1, an odd iteration i emits
// ITEM routes for depth (i + 1)/2. So reaching the items inside a MAX_GEAR_DEPTH
// category needs 2·MAX − 1 iterations, one more than reaching the category page.
//
// The old hardcoded 10 stopped exactly one short: it emitted the depth-6 category
// route but not the depth-6 item route, so those category pages rendered and every
// item link inside them 404'd.
export const GEAR_ROUTE_ITERATIONS = MAX_GEAR_DEPTH * 2 - 1;

// A category's depth is its document path — there is no parentId field.
//   gear/a/gear/b/gear/c → 3
//
// Only even indexes are counted: those are the collection segments. A doc id is
// always odd-indexed, so a category that happened to be *named* `gear` can't
// inflate its own depth.
export const categoryDepthFromPath = path => (path
    ? String(path).split('/').filter((segment, i) => i % 2 === 0 && segment === 'gear').length
    : 0);

// The alternating walk the two route generators below share.
//
// HeaderBreadcrumbs deliberately keeps its own copy rather than calling this: its
// table names the nested params subCollectionId0,1,2… where the router names them
// subCollectionId1,3,5…, and use-react-router-breadcrumbs zips its own params
// positionally so the two never have to agree. routerDepth.test.js asserts all
// three stay in step on the thing that does matter — which depths they reach.
//
// Calls `onCategory(relPath, absPath, depth)` when the walk arrives at a category
// level and `onItem(relPath, absPath, depth)` when it arrives at the items inside
// one. Depth 1 is special-cased: the root category segment is already in the
// starting path, so iteration 1 emits its items rather than the category itself.
const walkGearDepths = (iterations, {onCategory, onItem}) => {
    let relPath = ':collectionId';
    let absPath = 'gear/:collectionId';

    for (let i = 1; i <= iterations; i++) {
        if (i === 1) {
            onItem?.(relPath, absPath, 1);
        } else if (i % 2 === 0) {
            relPath += '/gear';
            absPath += '/gear';
            onCategory?.(relPath, absPath, i / 2 + 1);
        } else {
            relPath += `/:subCollectionId${i - 2}`;
            absPath += `/:subCollectionId${i - 2}`;
            onItem?.(relPath, absPath, (i + 1) / 2);
        }
    }
};

// Relative paths for the item-drawer routes — the drawer matches the same param
// shape as the full-page item route, which is what makes useParams() inside it
// resolve to the item rather than its parent category.
export const gearDrawerRoutePaths = (iterations = GEAR_ROUTE_ITERATIONS) => {
    const paths = [];
    walkGearDepths(iterations, {onItem: relPath => paths.push(`${relPath}/items/:id`)});
    return paths;
};

// Route descriptors for the /gear layout's children. Paths are relative (no
// leading `gear/`) because they mount under the layout route; redirect targets
// are absolute, so those keep it.
export const gearRouteDescriptors = (iterations = GEAR_ROUTE_ITERATIONS) => {
    const routes = [
        {path: ':id', element: 'collection'},
        {path: ':id/:type', isRedirect: 'gear/:id'},
        {path: ':id/:type/edit', element: 'editChecks'}
    ];

    walkGearDepths(iterations, {
        onCategory: (relPath, absPath) => {
            routes.push({path: `${relPath}/:id`, element: 'collection'});
            routes.push({path: `${relPath}/:id/:type`, isRedirect: `${absPath}/:id`});
            routes.push({path: `${relPath}/:id/:type/edit`, element: 'editChecks'});
        },
        onItem: (relPath, absPath, depth) => {
            // Only depth 1 gets the legacy bare `:collectionId/:id` alias.
            if (depth === 1) routes.push({path: `${relPath}/:id`, element: 'item'});
            routes.push({path: `${relPath}/items/:id`, element: 'item'});
            routes.push({path: `${relPath}/items/:id/:type`, isRedirect: `${absPath}/items/:id`});
            routes.push({path: `${relPath}/items/:id/:type/:checkId`, element: 'check'});
        }
    });

    return routes;
};
